import { type ColDef, type ICellRendererParams } from "ag-grid-community"
import { uniq } from "~/utils/tsUtils"
import { lowerCase, startCase } from "lodash"
import { useMemo, useState } from "react"
import { IncomingOrderStatus } from "~/constants/incomingOrdersStatus"
import { type IVariantSummary } from "~/hooks/useVariantTableData"
import { formatNumberWithSuffix, uniqueValueFormatter } from "~/utils/formatting"
import { cn } from "~/utils/helpers"
import {
  calculateIncomingStockStatus,
  type ExtraDataFilter,
  filterIncomingOrders,
  type IncomingOrder,
  type IncomingOrderDetail,
  incomingOrderStatusColor,
  skuQuantityInOrder,
} from "~/utils/incomingOrdersUtils"
import { api } from "~/utils/api"
import {
  DialogBodyV2,
  DialogContentV2,
  DialogFooterV2,
  DialogHeaderV2,
  DialogRoot,
  DialogTitle,
  DialogTrigger,
} from "~/components/reusable/Dialog"
import {
  createCustomColumn,
  createDateColumn,
  createStringColumn,
  createSumColumn,
  createUniqueStringColumn,
} from "~/utils/columnTemplates"
import { IncomingOrdersOverviewTableColumn } from "~/constants/incomingOrders"
import { createErpEtdColumn } from "~/components/purchaseOrder/incomingOrders/incomingOrderColumns"
import { addDays, isAfter, isBefore, subDays } from "date-fns"
import { isPast } from "~/utils/datetime"
import { IncomingOrdersStatusBadge } from "~/components/purchaseOrder/incomingOrders/IncomingOrdersStatusBadge"
import { ShipmentMethodIcon } from "~/components/icons"
import { getTailwindValue } from "~/utils/tailwind"
import IncomingOrdersTable, {
  type IncomingOrderRow,
  type IncomingOrdersTableDataSource,
} from "~/components/purchaseOrder/incomingOrders/IncomingOrdersTable"
import { CursorClick, Warehouse as WarehouseIcon } from "@phosphor-icons/react"
import { DateFormatType, formatDate } from "~/utils/datetime"
import IncomingOrdersDrawer from "~/components/purchaseOrder/incomingOrders/IncomingOrdersDrawer"
import { Button } from "~/components/reusable/Button"
import {
  useIncomingOrderExtraDataKeys,
  useExtraDataDisplayNameMap,
  useExtraDataTypeMap,
  useExtraDataAggregationMap,
} from "~/hooks/useSku"
import { incomingOrderDynamicColumns } from "~/components/planning/dynamicTableData/DynamicTableColumns"
import { Checkbox } from "~/components/reusable/Checkbox"
import { IncomingOrderSourceBadge } from "~/components/purchaseOrder/incomingOrders/IncomingOrderSourceBadge"
import { Badge } from "~/components/reusable/Badge"

type IncomingOrderCellRow = IVariantSummary & { skuId?: string }

export interface IncomingOrderCellRendererProps extends ICellRendererParams<IncomingOrderCellRow> {
  align?: "center" | "left" | "right"
  startDate?: Date
  endDate?: Date
  bucketLabel?: string
  warehouseId?: string
  shipmentMethod?: string | null
  // Active (incoming-order-extradata) filters (e.g. PO note) to apply to the freshly-fetched
  // detail orders so the dialog mirrors the parent view's filtered cell value (REKD-619).
  extraDataFilters?: ExtraDataFilter[]
  enableSkuScoping?: boolean
}

// Split out so the detail query and extra-data hooks only subscribe while the dialog is mounted.
export function IncomingOrderCellDialogBody({
  variantIds,
  skuId,
  startDate,
  endDate,
  showAll,
  showAllToggle,
  setShowAllToggle,
  bucketLabel,
  orders,
  open,
  warehouseId,
  warehouseIds,
  shipmentMethod,
  extraDataFilters,
}: {
  variantIds: string[]
  skuId?: string
  startDate: Date | undefined
  endDate: Date | undefined
  showAll: boolean
  showAllToggle: boolean
  setShowAllToggle: (v: boolean) => void
  bucketLabel: string | undefined
  orders: IncomingOrder[]
  open: boolean
  // Filters the server-side detail query to a single warehouse. Used by views with an active
  // warehouse selector (e.g. Forecast Calendar) so the dialog mirrors the cell value.
  warehouseId?: string
  // When set, fetched detail orders are filtered to these warehouses. Used by BuyTable to scope
  // a multi-warehouse cell click to a single warehouse's incoming orders.
  warehouseIds?: string[]
  // Mirrors the parent view's shipment-method filter so the dialog shows the same subset of
  // orders as the cell count (filtered client-side after the detail fetch).
  shipmentMethod?: string | null
  // Mirrors the parent view's (incoming-order-extradata) filters (e.g. PO note) so the dialog
  // shows the same subset as the cell count.
  extraDataFilters?: ExtraDataFilter[]
}) {
  const { data: incomingOrderExtraDataKeys } = useIncomingOrderExtraDataKeys()
  const { data: displayNameMap } = useExtraDataDisplayNameMap()
  const { data: typeMap } = useExtraDataTypeMap()
  const { data: aggregationMap } = useExtraDataAggregationMap()

  // Derive requested statuses from the cell's visible orders so the dialog shows the same set
  // (tile count would otherwise disagree with drawer rows when Arrived is toggled on).
  const requestedStatuses = useMemo(() => {
    const set = new Set<"incoming" | "arrived">()
    for (const o of orders) {
      if (o.status === "incoming" || o.status === "arrived") set.add(o.status)
    }
    return set.size === 0
      ? (["incoming"] as const)
      : (Array.from(set) as ("incoming" | "arrived")[])
  }, [orders])

  const { data: fullDetailOrders, isLoading: detailIsLoading } =
    api.variantTableData.incomingOrdersDetail.useQuery(
      {
        variantIds,
        statuses: requestedStatuses as ("incoming" | "arrived")[],
        startDate,
        endDate,
        warehouseId,
      },
      { enabled: variantIds.length > 0 },
    )
  const { data: hasExpectedDepartureDate } = api.purchaseOrder.hasExpectedDepartureDate.useQuery()
  const { data: warehouses } = api.warehouse.get.useQuery(undefined, {
    enabled: !!warehouseId,
  })
  const warehouseLabel = warehouseId
    ? (warehouses?.find((w) => w.id === warehouseId)?.name ?? warehouseId)
    : null
  // Disabled query reports isLoading=false with undefined data; gate on variantIds too so an
  // empty group row doesn't sit in a forever-loading state.
  const isLoadingOrders =
    variantIds.length > 0 && (detailIsLoading || fullDetailOrders === undefined)

  let detailOrders: IncomingOrderDetail[] = fullDetailOrders ?? []
  if (!showAll && (startDate || endDate)) {
    detailOrders = detailOrders.filter((order) => {
      const t = order.expectedArrivalDate
      if (startDate && !isAfter(t, subDays(startDate, 1))) return false
      if (endDate && !isBefore(t, addDays(endDate, 1))) return false
      return true
    })
  }
  if (warehouseIds && warehouseIds.length > 0) {
    const allowed = new Set(warehouseIds)
    detailOrders = detailOrders.filter((o) => allowed.has(o.warehouseId))
  }
  if (shipmentMethod) {
    const target = shipmentMethod.toLowerCase()
    detailOrders = detailOrders.filter((o) => o.shipmentMethod?.toLowerCase() === target)
  }
  if (extraDataFilters && extraDataFilters.length > 0) {
    detailOrders = filterIncomingOrders(detailOrders, { extraDataFilters })
  }
  if (skuId) {
    detailOrders = detailOrders.filter((o) => skuQuantityInOrder(o, skuId) > 0)
  }

  const columnDefs = useMemo((): ColDef<IncomingOrderRow>[] => {
    const baseColumns: ColDef<IncomingOrderRow>[] = [
      createUniqueStringColumn({
        colId: IncomingOrdersOverviewTableColumn.ProductionOrderId,
        field: "productionOrderId",
        headerName: "Order ID",
        tooltipTitle: "Production Orders",
        width: 185,
        cellStyle: (params) => {
          if (
            params.data?.status === "incoming" &&
            params.data?.expectedArrivalDate &&
            isPast(params.data.expectedArrivalDate)
          ) {
            return {
              borderLeft: "2px solid " + getTailwindValue("--color-red-500"),
            }
          }
        },
      }),
      createCustomColumn({
        colId: IncomingOrdersOverviewTableColumn.Status,
        field: "status",
        headerName: "Status",
        width: 120,
        valueGetter: (params) => {
          if (params.data?.status === "arrived") {
            return IncomingOrderStatus.Arrived
          }
          if (params.data?.status === "incoming") {
            const expectedDate = new Date(params.data.expectedArrivalDate)

            if (isPast(expectedDate)) {
              return IncomingOrderStatus.Delayed
            }
            return IncomingOrderStatus.OnTime
          }
          return params.data?.status || "Unknown"
        },
        cellRenderer: (params: any) => {
          if (!params.value || params.node?.group) return null
          return (
            <div className="flex items-center gap-2 leading-5 h-[40px]">
              <IncomingOrdersStatusBadge status={params.value as IncomingOrderStatus} size="sm" />
            </div>
          )
        },
      }),
      createErpEtdColumn<IncomingOrderRow>({ hide: !hasExpectedDepartureDate, width: 112 }),
      createDateColumn({
        colId: IncomingOrdersOverviewTableColumn.ExpectedArrivalDate,
        field: "expectedArrivalDate",
        headerName: "ETA",
        width: 112,
        aggFunc: "unique",
        tooltipTitle: "Expected Arrival Dates",
        filter: true,
      }),
      createSumColumn({
        colId: IncomingOrdersOverviewTableColumn.TotalQuantity,
        field: "quantity",
        headerName: "Quantity",
        headerSubtitle: "units",
        alignment: "left",
        width: 110,
      }),
      createStringColumn({
        colId: IncomingOrdersOverviewTableColumn.ShipmentMethod,
        field: "shipmentMethod",
        headerName: "Shipment",
        width: 160,
        valueFormatter: (params) =>
          startCase(lowerCase(uniqueValueFormatter(params, "shipment methods"))),
        cellRenderer: (params: any) =>
          params.node?.group || !params.value ? undefined : (
            <div className="flex items-center gap-2 leading-5 h-[40px]">
              <ShipmentMethodIcon
                color={getTailwindValue("--color-fg-subtle")}
                shipmentMethod={lowerCase(params.value)}
              />
              <span>{startCase(lowerCase(uniqueValueFormatter(params, "shipment methods")))}</span>
            </div>
          ),
      }),
    ]

    const dynamicCols = incomingOrderDynamicColumns<IncomingOrderRow>(
      incomingOrderExtraDataKeys ?? [],
      displayNameMap,
      typeMap,
      aggregationMap,
    )

    const sourceColumn: ColDef<IncomingOrderRow> = {
      colId: "source",
      headerName: "Source",
      field: "source",
      width: 110,
      cellRenderer: (params: any) => {
        if (params.node?.group || !params.value) return null
        return (
          <div className="flex items-center h-[40px]">
            <IncomingOrderSourceBadge source={params.value as "Hakio" | "ERP"} />
          </div>
        )
      },
    }

    return [...baseColumns, sourceColumn, ...dynamicCols]
  }, [
    incomingOrderExtraDataKeys,
    displayNameMap,
    typeMap,
    aggregationMap,
    hasExpectedDepartureDate,
  ])

  return (
    <>
      <DialogHeaderV2>
        <div className="flex items-center gap-2">
          <DialogTitle>
            {bucketLabel
              ? `Incoming orders: ${bucketLabel}`
              : startDate || endDate
                ? `Incoming Orders ${formatDate(orders[0]?.expectedArrivalDate, DateFormatType.MonthYearFull)}`
                : "Incoming Orders"}
          </DialogTitle>
          {warehouseLabel && (
            <Badge variant="neutral" size="sm">
              <WarehouseIcon size={14} color={getTailwindValue("--color-fg-subtle")} />
              Warehouse: {warehouseLabel}
            </Badge>
          )}
          {shipmentMethod && (
            <Badge variant="neutral" size="sm">
              <ShipmentMethodIcon
                shipmentMethod={lowerCase(shipmentMethod)}
                color={getTailwindValue("--color-fg-subtle")}
                size={14}
              />
              Shipment: {startCase(lowerCase(shipmentMethod))}
            </Badge>
          )}
        </div>
      </DialogHeaderV2>
      <DialogBodyV2 className="max-h-[40vh] h-[40vh] overflow-hidden">
        <div className="flex flex-row justify-between shrink-0">
          <span className={"paragraph-base text-fg-subtle flex items-center gap-1"}>
            <CursorClick className={"text-fg-muted"} /> Click any order to view more details
          </span>
          {startDate && endDate && (
            <span className="flex paragraph-base items-center text-center gap-2">
              Show all orders
              <Checkbox
                checked={showAllToggle}
                onCheckedChange={(checked) => setShowAllToggle(Boolean(checked))}
              />
            </span>
          )}
        </div>
        <div className="flex-1 min-h-0">
          {/* Conditional render: unmounts grid immediately on close, triggering AG Grid's
              bulk destroy path (no per-row animations) instead of row-removal animations
              which block the main thread for seconds. Radix keeps the portal alive for its
              200ms close animation, so the user sees a smooth fade on an empty container. */}
          {open && (
            <IncomingOrdersTable
              id="incoming-orders-dialog"
              skuId={skuId}
              columnDef={columnDefs}
              dataSource={
                [{ incomingOrders: detailOrders }] satisfies IncomingOrdersTableDataSource[]
              }
              showPagination={detailOrders.length > 100}
              loading={isLoadingOrders}
              fitGridWidth
            />
          )}
        </div>
      </DialogBodyV2>
      <DialogFooterV2>
        <IncomingOrdersDrawer
          trigger={
            <Button className="w-full" variant="neutral" size="sm">
              View Summary
            </Button>
          }
          incomingOrders={detailOrders}
          skuId={skuId}
        />
      </DialogFooterV2>
    </>
  )
}

export function IncomingOrderCellRenderer({
  align,
  startDate,
  endDate,
  bucketLabel,
  warehouseId,
  shipmentMethod,
  extraDataFilters,
  enableSkuScoping,
  ...params
}: IncomingOrderCellRendererProps) {
  const [open, setOpen] = useState(false)
  const [showAllToggle, setShowAllToggle] = useState(false)

  const showAll = showAllToggle || (!startDate && !endDate)

  const skuId = enableSkuScoping ? params.data?.skuId : undefined

  const variantIds = useMemo(() => {
    if (params.data?.variantId) return [params.data.variantId]
    if (params.node?.group) {
      const ids = params.node.allLeafChildren
        ?.map((child: any) => (child.data as IVariantSummary | undefined)?.variantId)
        .filter((id: unknown): id is string => !!id)
      return [...new Set(ids ?? [])]
    }
    return []
  }, [params.data?.variantId, params.node?.group, params.node?.allLeafChildren])

  const allOrders: IncomingOrder[] = params.value?.incomingOrders ?? []
  const orders = useMemo(() => {
    if (showAll || !startDate || !endDate) return allOrders
    const lower = subDays(startDate, 1)
    const upper = addDays(endDate, 1)
    return allOrders.filter(
      (order) =>
        isAfter(order.expectedArrivalDate, lower) && isBefore(order.expectedArrivalDate, upper),
    )
  }, [allOrders, showAll, startDate, endDate])

  const statuses = useMemo(
    () =>
      (
        uniq(orders.map((order) => calculateIncomingStockStatus(order))) as IncomingOrderStatus[]
      ).sort((a, b) => a.localeCompare(b)),
    [orders],
  )

  // Gate on the unfiltered count so the early-return only fires when there are no orders at all,
  // not merely none in the current date window.
  const allOrdersCount = params.value?.allOrdersCount ?? allOrders.length
  if (!params.value?.value && !allOrdersCount) return ""

  const openChange = (nextOpen: boolean) => {
    if (nextOpen === false) {
      setShowAllToggle(false)
    }
    setOpen(nextOpen)
  }

  return (
    <div
      className={cn(
        "leading-5 h-[40px] flex items-center ml-1",
        align === "center" && "justify-center ml-0",
        align === "right" && "justify-end ml-0 mr-1",
      )}
    >
      <DialogRoot open={open} onOpenChange={openChange}>
        <DialogTrigger asChild>
          <div
            className={cn(
              "p-1.5 h-[24px] cursor-pointer shadow-card-rest rounded-[6px] bg-gradient-to-b from-white to-subtle dark:from-zinc-800/90 dark:to-zinc-950 flex flex-row items-center gap-1.5",
              open && "shadow-button-neutral-focus",
            )}
          >
            <div className="flex flex-row items-center gap-0">
              {statuses.length > 0 ? (
                statuses.map((status, index) => (
                  <div
                    key={index}
                    className="w-1 h-3 rounded-[2px] outline outline-background-base"
                    style={{
                      backgroundColor: incomingOrderStatusColor(status as IncomingOrderStatus),
                      zIndex: index,
                    }}
                  ></div>
                ))
              ) : (
                <div
                  className="w-1 h-3 rounded-[2px] outline outline-background-base"
                  style={{
                    backgroundColor: "grey",
                  }}
                ></div>
              )}
            </div>
            {params.value ? formatNumberWithSuffix(params.value.value, 1) : ""}
            {params.value?.value == 0 &&
              (bucketLabel != null || !showAll) &&
              ` ${bucketLabel ? bucketLabel.split(" (")[0] : "in period"}`}
          </div>
        </DialogTrigger>
        <DialogContentV2 className="max-w-[min(95vw,1280px)]">
          <IncomingOrderCellDialogBody
            variantIds={variantIds}
            skuId={skuId}
            startDate={startDate}
            endDate={endDate}
            showAll={showAll}
            showAllToggle={showAllToggle}
            setShowAllToggle={setShowAllToggle}
            bucketLabel={bucketLabel}
            orders={orders}
            open={open}
            warehouseId={warehouseId}
            shipmentMethod={shipmentMethod}
            extraDataFilters={extraDataFilters}
          />
        </DialogContentV2>
      </DialogRoot>
    </div>
  )
}
